import * as React from 'react';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const TooltipProvider = ({
  delayDuration = 400,
  skipDelayDuration = 300,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider>) => (
  <TooltipPrimitive.Provider
    delayDuration={delayDuration}
    skipDelayDuration={skipDelayDuration}
    {...props}
  />
);

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = ({
  ref,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) => (
  <TooltipPrimitive.Trigger ref={ref} {...props} />
);

const tooltipVariants = cva(
  'z-50 origin-[--radix-tooltip-content-transform-origin] rounded-md bg-bg-tooltip text-text-primary-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
  {
    variants: {
      variant: { default: 'px-3 py-1.5 text-sm', timestamp: 'z-[9999]', info: 'px-2 py-1 text-sm' },
    },
    defaultVariants: { variant: 'default' },
  }
);

const arrowClassName =
  'z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-bg-tooltip fill-bg-tooltip';

const variantSideOffset = { default: 4, timestamp: 8, info: 4 } as const;

const TooltipContent = ({
  className,
  sideOffset,
  variant = 'default',
  showArrow = false,
  ref,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> &
  VariantProps<typeof tooltipVariants> & { showArrow?: boolean }) => {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset ?? variantSideOffset[variant ?? 'default']}
        className={cn(tooltipVariants({ variant }), className)}
        {...props}
      >
        {props.children}
        {showArrow && <TooltipPrimitive.Arrow className={arrowClassName} />}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
};

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
